import { z } from 'zod';
import type {
  activitiesActivityFlowProjectPathObj,
  projectPathObj,
  rootActivityPathObj,
} from '../models/pinpoint-path';

export const KsLocalGovernmentBasicInformationScenePinpointFieldEnum = z.enum([
  'ks.local-government.organization-in-charge',
  'ks.local-government.people-in-charge',
  'ks.local-government.business-manager',
  'ks.local-government.other-organization-in-charge',
  'ks.local-government.related-organizations',
  'ks.local-government.project-number',
  'ks.local-government.project-group',
  'ks.local-government.fund-sheet-type',
  'ks.local-government.fund-sheet-number',
  'ks.local-government.fund-sheet-branch-number',
  'ks.local-government.operation-form',
  'ks.local-government.business-form',
  'ks.local-government.overview',
  'ks.local-government.fund-necessity-type',
  'ks.local-government.fund-necessity-reasons-for-local-government',
  'ks.local-government.fund-scheduled-end-period-for-local-government',
  'ks.local-government.end-date-of-new-application-for-local-government',
  'ks.local-government.status-of-response-by-ministries',
  'ks.local-government.remark',
  'ks.local-government.old-project-number',
  'ks.local-government.project-reference',
  'ks.local-government.legal-basis',
  'ks.local-government.beneficiary-info',
  'ks.local-government.related-project-for-other-ministry',
]);

export const KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum =
  z.enum(['ks.local-government.composition-backgrounds-title']);

export const KsLocalGovernmentActivitiesScenePinpointFieldEnum = z.enum([
  'ks.local-government.cabinet-decision-with-defined-indicators-name',
  'ks.local-government.cabinet-decision-with-defined-indicators-url',
  'ks.local-government.cabinet-decision-with-defined-indicators-relevant-part',
  'ks.local-government.activity-title',
  'ks.local-government.activity',
  'ks.local-government.activities-and-outcomes-title',
]);

export const KsLocalGovernmentActivityFlowScenePinpointFieldEnum = z.enum([
  'ks.local-government.indicator-title',
  'ks.local-government.indicator-description',
  'ks.local-government.activity-and-result-target',
  'ks.local-government.achievement-for-qualitative-outcome',
  'ks.local-government.indicator-setting-reason',
  'ks.local-government.reason-for-setting-qualitative-outcome',
  'ks.local-government.reason-why-outcomes-cannot-be-multiple',
  'ks.local-government.connection-to-next-outcome',
]);

export const KsLocalGovernmentExecutionProgressScenePinpointFieldEnum = z.enum([
  'ks.local-government.income-and-expenditure-table',
  'ks.local-government.fund-business-table',
  'ks.local-government.individual-table',
]);

export const KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum = z.enum([
  'ks.local-government.related-material-image',
  'ks.local-government.related-material-remark',
]);

export const KsLocalGovernmentPinpointFieldEnum = z.enum([
  ...KsLocalGovernmentBasicInformationScenePinpointFieldEnum.options,
  ...KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum.options,
  ...KsLocalGovernmentActivityFlowScenePinpointFieldEnum.options,
  ...KsLocalGovernmentActivitiesScenePinpointFieldEnum.options,
  ...KsLocalGovernmentExecutionProgressScenePinpointFieldEnum.options,
  ...KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum.options,
]);

export const KsLocalGovernmentPinpointMultipleFieldEnum = z.enum([
  KsLocalGovernmentActivitiesScenePinpointFieldEnum.Enum[
    'ks.local-government.activity'
  ],
  ...KsLocalGovernmentActivityFlowScenePinpointFieldEnum.options,
]);

type ProjectPinpointMappingType = {
  path: z.infer<typeof projectPathObj>;
  field: Exclude<
    | z.infer<typeof KsLocalGovernmentBasicInformationScenePinpointFieldEnum>
    | z.infer<
        typeof KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum
      >
    | z.infer<typeof KsLocalGovernmentActivitiesScenePinpointFieldEnum>
    | z.infer<typeof KsLocalGovernmentExecutionProgressScenePinpointFieldEnum>
    | z.infer<typeof KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum>,
    z.infer<typeof KsLocalGovernmentPinpointMultipleFieldEnum>
  >;
};

type KsLocalGovernmentPinpointMultipleFieldEnumFieldEnumType =
  typeof KsLocalGovernmentPinpointMultipleFieldEnum.Enum;

export type KsLocalGovernmentActivityFlowPinpointMappingType = {
  path: z.infer<typeof activitiesActivityFlowProjectPathObj>;
  field: z.infer<typeof KsLocalGovernmentActivityFlowScenePinpointFieldEnum>;
};

export type KsLocalGovernmentActivity = {
  path: z.infer<typeof rootActivityPathObj>;
  field: KsLocalGovernmentPinpointMultipleFieldEnumFieldEnumType['ks.local-government.activity'];
};

export type KsLocalGovernmentMultiplePinpointMappingType =
  | KsLocalGovernmentActivity
  | KsLocalGovernmentActivityFlowPinpointMappingType;

export type KsLocalGovernmentPinpointMappingType =
  | ProjectPinpointMappingType
  | KsLocalGovernmentMultiplePinpointMappingType;
